import React from "react";
import { Box, Typography, useTheme } from "@material-ui/core";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";
import { reportSentryClientError } from "../../services/sentry";

type Props = {
  data: any;
};

export default function BarChart({ data }: Props) {
  const theme = useTheme();

  // Vaidate data coming from DatoCMS
  if (
    data === null ||
    typeof data !== "object" ||
    !data.bars ||
    !Array.isArray(data.bars)
  ) {
    reportSentryClientError(
      new Error(
        "Invalid device-features-bar-chart charBart json data. Check DatoCMS"
      )
    );
    return null;
  }

  const [ref, barChartInView] = useInView({
    threshold: 1,
    triggerOnce: true
  });

  const animationDuration = 0.75;

  return (
    <Box
      // @ts-ignore
      ref={ref}
    >
      {data.bars?.map((bar, i) => (
        <Box key={i} mb={{ xs: 1, sm: 2 }}>
          {/* Bar Chart: Brand label */}
          <Typography
            style={{
              color: theme.palette.text.primary,
              fontSize: "1.4rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "1.5px"
            }}
          >
            {bar.name}
          </Typography>

          {/* Bar Chart: Graph bar */}
          <Box display="flex" alignItems="center">
            <Box flex={1}>
              <motion.div
                variants={{
                  initial: {
                    width: 0
                  },
                  visible: {
                    width: `${100 * (bar.percentage || 1)}%`,
                    transition: {
                      duration: animationDuration,
                      ease: "easeIn"
                      // delay: i * 0.15
                    }
                  }
                }}
                animate={barChartInView ? "visible" : "initial"}
                style={{
                  height: theme.spacing(1),
                  borderRadius: "4px",
                  background:
                    bar.color === "main"
                      ? "linear-gradient(90deg, #FFBB00 0%, #FF7A00 100%)"
                      : theme.palette.grey[600]
                }}
              />
            </Box>
            {bar.label && (
              <motion.span
                style={{
                  color: theme.palette.text.primary,
                  fontSize: bar.color === "main" ? "1.8rem" : "1.5rem",
                  fontWeight: bar.color === "main" ? 600 : 400,
                  marginLeft: theme.spacing(2),
                  fontFamily: "Roobert, Arial"
                }}
                variants={{
                  initial: {
                    scale: 0.5,
                    opacity: 0
                  },
                  visible: {
                    scale: 1,
                    opacity: 1,
                    transition: {
                      duration: animationDuration,
                      ease: "easeIn"
                    }
                  }
                }}
                animate={barChartInView ? "visible" : "initial"}
              >
                {bar.label}
              </motion.span>
            )}
          </Box>
        </Box>
      ))}

      {/* Bar Chart: Bottom Description */}
      {data.description && (
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            {data.description}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
